import React, { useState, useEffect } from 'react'
import SplitCode from '../SplitCode/SplitCode';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faUsers, faUserPlus, faChevronLeft, faCheck, faTrash, faShareFromSquare, faLock, faLockOpen } from '@fortawesome/free-solid-svg-icons';
import { isMobile, isIOS } from 'react-device-detect';
import { useLocation } from "react-router-dom";

const GroupsView = ({ userGUID, setSelectedGroupGUIDParent, getSchedule }) => {

    const { search } = useLocation();
    const params = new URLSearchParams(search);
    const groupcode = String(params.get("groupcode") || '').replace(/\D/g, '').slice(0, 8);

    const [groupJson, setGroupJson] = useState(localStorage.getItem('groupJson') ? JSON.parse(localStorage.getItem('groupJson')) : []);
    const [selectedGroupGUID, setSelectedGroupGUID] = useState(localStorage.getItem('selectedGroupGUID') ? localStorage.getItem('selectedGroupGUID') : '');
    const [action, setAction] = useState('');
    const [joinGroupCode, setJoinGroupCode] = useState(groupcode || '');
    const [createGroupName, setCreateGroupName] = useState('');
    const [errorMessage, setErrorMessage] = useState('');

    const getGroups = async () => {
        if (!userGUID) return;
        fetch(`/api/group/${userGUID}`)
            .then((response) => {
                if (response.ok) { // Checks if the status is in the range 200-299
                    return response.json();
                } else {
                    return null;
                }
            })
            .then((results) => {
                if (results) {
                    setGroupJson(results)
                    if (results.length === 1) setSelectedGroupGUID(results[0].groupGUID);
                    if (results.length === 0) setSelectedGroupGUID('');
                    // if (results.length > 1 && results.findIndex(group => group.groupGUID === selectedGroupGUID) === -1) setSelectedGroupGUID('');
                }
            })
            .catch((error) => {
            });
    };

    const createGroup = async () => {
        setErrorMessage('');
        fetch(`/api/group/`, {
            method: 'POST',
            body: JSON.stringify({
                groupName: createGroupName,
                groupOwnerUserGUID: userGUID,
            }),
            headers: { 'Content-Type': 'application/json' },
        })
            .then(async (response) => {
                if (response.ok) { // Checks if the status is in the range 200-299
                    const groupGUID = await response.text();
                    setCreateGroupName('');
                    setAction('');
                    getGroups();
                    setSelectedGroupGUID(groupGUID);
                } else {
                    setErrorMessage('Unable to create group, please try again later.')
                    return null;
                }
            })
            .catch((error) => {
            });
    };

    const joinGroup = async () => {
        setErrorMessage('');
        fetch(`/api/group-member/`, {
            method: 'POST',
            body: JSON.stringify({
                groupCode: joinGroupCode,
                groupMemberUserGUID: userGUID,
            }),
            headers: { 'Content-Type': 'application/json' },
        })
            .then(async (response) => {
                if (response.ok) { // Checks if the status is in the range 200-299
                    const groupGUID = await response.text();
                    setJoinGroupCode('');
                    setAction('');
                    getGroups();
                    setSelectedGroupGUID(groupGUID);
                } else {
                    setErrorMessage('Unable to join group. Please confirm code and try again later.')
                    return null;
                }
            })
            .catch((error) => {
            });
    };

    const toggleLockGroup = async () => {
        setErrorMessage('');
        fetch(`/api/group/${selectedGroupGUID}/${userGUID}`, {
            method: 'PUT',
            body: JSON.stringify({
                groupName: selectedGroup.groupName,
                groupLocked: !selectedGroup.groupLocked,
            }),
            headers: { 'Content-Type': 'application/json' },
        })
            .then((response) => {
                if (response.ok) { // Checks if the status is in the range 200-299
                    getGroups();
                } else {
                    setErrorMessage('Unable to modify group. Please try again later.')
                    return null;
                }
            })
            .catch((error) => {
            });
    };

    const removeGroup = async (groupGUID) => {
        setErrorMessage('');
        fetch(`/api/group/${groupGUID}/${userGUID}`, {
            method: 'DELETE',
        })
            .then((response) => {
                if (response.ok) { // Checks if the status is in the range 200-299
                    getGroups();
                    getSchedule();
                } else {
                    return null;
                }
            })
            .catch((error) => {
                setErrorMessage('Unable to remove group, please try again later.');
            })
    };

    const removeGroupMember = async (memberGUID) => {
        setErrorMessage('');
        fetch(`/api/group-member/${memberGUID}/${userGUID}`, {
            method: 'DELETE',
        })
            .then((response) => {
                if (response.ok) { // Checks if the status is in the range 200-299
                    getGroups();
                    getSchedule();
                } else {
                    return null;
                }
            })
            .catch((error) => {
                setErrorMessage('Unable to remove group member, please try again later.');
            });
    };

    useEffect(() => {
        setErrorMessage('');
    }, [action]);

    useEffect(() => {
        getGroups();
    }, []);

    useEffect(() => {
        localStorage.setItem('selectedGroupGUID', selectedGroupGUID);
        setSelectedGroupGUIDParent(selectedGroupGUID);
    }, [selectedGroupGUID]);

    const shareMessage = async ({ title, message, url }) => {
        const text = `${message?.trim() ?? ''}${message ? '\n' : ''}`.trim();
        const textUrl = `${message?.trim() ?? ''}${message ? '\n' : ''}${url ?? ''}`.trim();

        // Best: native share sheet (iOS/Android/Chrome/Edge over HTTPS + user gesture)
        if (navigator.share) {
            try {
                await navigator.share({ title, text, url }); // include url AND text (some clients parse differently)
                return true;
            } catch (e) {
                // user canceled or error – continue to fallbacks
            }
        }

        // Fallback 1: SMS (works on mobile browsers)
        const smsUrl = isIOS
            ? `sms:&body=${encodeURIComponent(textUrl)}`       // iOS uses &body
            : `sms:?body=${encodeURIComponent(textUrl)}`;      // Android uses ?body
        if (isMobile) {
            try { window.location.href = smsUrl; return false; } catch { }
        }

        // Fallback 2: Email (desktop/mobile)
        const mailto = `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(textUrl)}`;
        try { window.location.href = mailto; return false; } catch { }

        // Fallback 3: Copy to clipboard (desktop safe option)
        try {
            await navigator.clipboard.writeText(textUrl);
            alert('Copied to clipboard. Paste into Messages/Email to share.');
        } catch {
            // Last resort: open a prompt so the user can copy manually
            window.prompt('Copy this text:', textUrl);
        }
        return false;
    }

    const selectedGroup = selectedGroupGUID ? groupJson.find(group => group.groupGUID === selectedGroupGUID) : null;

    return (
        <div id="groupsContainer">
            {action === 'join' && <div className='input-container groups-container'>

                <h2>Enter Group Code to Join</h2>
                <SplitCode
                    value={joinGroupCode} onChange={setJoinGroupCode}
                />
                <button
                    disabled={String(joinGroupCode).length !== 8}
                    onClick={joinGroup}
                    style={{
                        //  flex: '1 1',
                        width: '100%',
                        padding: '5px 5px',
                        marginTop: '10px'
                    }}
                    className="btn btn-primary"
                >
                    <FontAwesomeIcon icon={faCheck} /> Join Group
                </button>
                <button
                    onClick={() => {
                        setAction('');
                        setJoinGroupCode('');
                    }}
                    style={{
                        //  flex: '1 1',
                        width: '100%',
                        padding: '5px 5px',
                        marginTop: '10px'
                    }}
                    className="btn"
                >
                    <FontAwesomeIcon icon={faChevronLeft} /> Cancel
                </button>
                {errorMessage && <div className='error-message'>
                    {errorMessage}
                </div>}
            </div>}

            {action === 'create' && <div className='input-container groups-container'>
                <h2>Create a Group</h2>
                <input
                    value={createGroupName}
                    name="createGroup"
                    id='selectedGroup'
                    maxLength={50}
                    placeholder='Group Name'
                    style={{
                        fontSize: '30px',
                        height: '60px',
                        textAlign: 'center'
                    }}
                    onChange={(e) => {
                        setCreateGroupName(e.target.value)
                    }}
                />
                <button
                    disabled={createGroupName === ''}
                    onClick={createGroup}
                    style={{
                        //  flex: '1 1',
                        width: '100%',
                        padding: '5px 5px',
                        marginTop: '10px'
                    }}
                    className="btn btn-primary"
                >
                    <FontAwesomeIcon icon={faCheck} /> Create Group
                </button>
                <button
                    onClick={() => {
                        setAction('');
                        setCreateGroupName('');
                    }}
                    style={{
                        //  flex: '1 1',
                        width: '100%',
                        padding: '5px 5px',
                        marginTop: '10px'
                    }}
                    className="btn"
                >
                    <FontAwesomeIcon icon={faChevronLeft} /> Cancel
                </button>
                {errorMessage && <div className='error-message'>
                    {errorMessage}
                </div>}
            </div>}

            {action === '' &&
                <div className="input-container groups-container">

                    <button
                        onClick={() => setAction('join')}
                        style={{
                            //  flex: '1 1',
                            width: '100%',
                            padding: '5px 5px',
                            marginBottom: '10px'
                        }}
                        className="btn btn-primary"
                    >
                        <FontAwesomeIcon icon={faUserPlus} /> Join Existing Group
                    </button>

                    <button
                        onClick={() => setAction('create')}
                        style={{
                            //  flex: '1 1',
                            width: '100%',
                            padding: '5px 5px',
                            marginBotom: '10px'
                        }}
                        className="btn btn-primary"
                    >
                        <FontAwesomeIcon icon={faUsers} /> Create a Group
                    </button>

                    {groupJson.length > 0 &&
                        <select
                            value={selectedGroupGUID}
                            name="groupGUID"
                            id='selectedGroup'
                            style={{
                                marginTop: '30px'
                            }}
                            onChange={(e) => {
                                setSelectedGroupGUID(e.target.value)
                            }}
                        >
                            {groupJson.length > 1 && <option value="">My Groups</option>}
                            {groupJson.map(group => (
                                <option key={group.groupGUID} value={group.groupGUID}>{group.groupName}</option>
                            ))}
                        </select>}

                    {selectedGroup && <div id="selected-group-content">

                        <h1 style={{
                            fontSize: '60px',
                            margin: '10px 0'
                        }}>{selectedGroup.groupLocked ? '####-####' : selectedGroup.groupCode}</h1>

                        <button
                            disabled={selectedGroup.groupLocked}
                            onClick={() => shareMessage({
                                title: 'Join my EDC Planning Group!',
                                message: `Login and plan set lists we want to check out together! Use this code to join my group: ${selectedGroup.groupCode}`,
                                url: `https://edc.sunsetskycreative.com?groupcode=${selectedGroup.groupCode.replace('-', '')}`
                            })}
                            style={{
                                //  flex: '1 1',
                                width: '100%',
                                padding: '5px 5px',
                                marginBotom: '10px'
                            }}
                            className="btn btn-primary"
                        >
                            <FontAwesomeIcon icon={faShareFromSquare} /> {selectedGroup.groupLocked ? 'Group is Locked' : 'Share This Code to Join'}
                        </button>

                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'row',
                                marginTop: '10px',
                                padding: '10px'
                            }}
                        >
                            <h2
                                style={{
                                    flex: '1 1',
                                    margin: '10px 0',
                                    textAlign: 'left'
                                }}
                            >Group Members</h2>
                            {selectedGroup.isOwner ? <button
                                style={{
                                    flex: '0 0 40px',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    border: '0 none !important'
                                }}
                                onClick={toggleLockGroup}
                                className="btn"
                            >
                                <FontAwesomeIcon icon={selectedGroup.groupLocked ? faLock : faLockOpen} style={{ margin: 0 }} />
                            </button> : <></>}
                        </div>
                        <div className="group-member-container">
                            {selectedGroup.members
                                .sort((a, b) => a.name.localeCompare(b.name))
                                .map(member => (
                                    <div key={member.groupMemberGUID} className="group-member">
                                        <div className="group-member-name">
                                            {member.name}
                                        </div>
                                        <div className="group-member-buttons">
                                            {((selectedGroup.isOwner && !member.isMe) ||
                                                (!selectedGroup.isOwner && member.isMe))
                                                ? <button
                                                    onClick={() => removeGroupMember(member.groupMemberGUID)}
                                                    className="btn btn-danger"
                                                >
                                                    <FontAwesomeIcon icon={faTrash} /> Remove {member.isMe ? 'Myself' : ''}
                                                </button>
                                                :
                                                <>
                                                    {selectedGroup.members.length === 1 ?
                                                        <button
                                                            onClick={() => removeGroup(selectedGroup.groupGUID)}
                                                            className="btn btn-danger"
                                                        >
                                                            <FontAwesomeIcon icon={faTrash} /> Delete Group
                                                        </button>
                                                        :
                                                        <>{selectedGroup.isOwner || member.isOwner ? 'Owner' : ''}</>
                                                    }
                                                </>}
                                        </div>
                                    </div>
                                ))}
                        </div>
                        {errorMessage && <div className='error-message'>
                            {errorMessage}
                        </div>}
                    </div>}

                </div>}

        </div>
    )
}

export default GroupsView;